/**
 * Given a public model's ID, return the list of projects that depend on it.
 */

import { useMemo } from 'react';

import { DbtDagNode } from '@dbt-labs/dbt-dag';

import { useConsumerProjects } from './discovery/relatedProjects';

export type ExternalNodeResult = {
  /**
   * If true, the external nodes are still loading.
   */
  loading: boolean;
  /**
   * Data will be displayed if loading is false.
   */
  data: null | ExternalConnections;
  /**
   * Error response for the external nodes.
   */
  error: null | any;
};

export type ExternalNode = Omit<DbtDagNode, 'parents'> & {
  dbtCoreProject: string;
  /**
   * The number of models which reference the public model.
   */
  numDependentModels?: number;
};

export type ExternalConnections = {
  parents: ExternalNode[];
  children: ExternalNode[];
};

export function useDependentProjects(environmentId: number, pause?: boolean) {
  const {
    fetching,
    data: consumerProjectsData,
    error,
  } = useConsumerProjects({
    environmentId,
    pause,
  });
  return useMemo(() => {
    const map: Record<string, ExternalConnections> = {};
    const consumerProjects = consumerProjectsData?.consumerProjects || [];

    consumerProjects.forEach((project) => {
      (project.producerPublicModels || []).forEach((model) => {
        map[model.uniqueId] ||= {
          children: [],
          parents: [],
        };
        map[model.uniqueId].children.push({
          id: project.consumerProjectId.toString(),
          resourceType: 'project',
          environmentId: project.consumerEnvironmentId,
          dbtCloudProject: project.consumerCloudProject,
          dbtCoreProject: project.consumerCoreProject,
          label: project.consumerCloudProject,
          projectId: project.consumerProjectId,
          numDependentModels: model.numEdges,
          data: {
            defaultEnvironmentId: project.consumerEnvironmentId,
          },
        });
      });
    });

    return {
      fetching,
      error,
      data: fetching || error ? null : map,
    };
  }, [consumerProjectsData?.consumerProjects, fetching, error]);
}
